import { TouchEvent, useRef } from 'react'

export interface UseSwipeNavigationOptions {
  enabled: boolean
  onSwipeLeft: () => void
  onSwipeRight: () => void
}

// Swiping pages the gallery only on mobile and tablet layouts; desktop has the
// arrow buttons and a mouse.
const SWIPE_MEDIA_QUERY = '(max-width: 1024px)'
// A horizontal move shorter than this is a tap or a wobble, not a swipe.
const SWIPE_MIN_DISTANCE = 50
// The horizontal move has to clearly beat the vertical one, so scrolling the
// page with a slightly diagonal finger never changes the slide.
const SWIPE_AXIS_RATIO = 1.5

type SwipeState = {
  startX: number
  startY: number
  // Set as soon as a second finger lands; that gesture is a pinch, not a
  // swipe, even once one finger lifts.
  cancelled: boolean
}

// Detects a one-finger horizontal swipe from the touch events of the element
// the returned handlers are spread onto. It never prevents default, so vertical
// scrolling over the element keeps working.
export const useSwipeNavigation = ({
  enabled,
  onSwipeLeft,
  onSwipeRight,
}: UseSwipeNavigationOptions) => {
  const swipeRef = useRef<SwipeState | null>(null)

  const onTouchStart = (event: TouchEvent<HTMLElement>) => {
    if (!enabled || !window.matchMedia(SWIPE_MEDIA_QUERY).matches) {
      swipeRef.current = null

      return
    }

    if (event.touches.length > 1) {
      if (swipeRef.current) {
        swipeRef.current.cancelled = true
      }

      return
    }

    const touch = event.touches.item(0)

    swipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      cancelled: false,
    }
  }

  const onTouchEnd = (event: TouchEvent<HTMLElement>) => {
    const swipe = swipeRef.current

    if (!swipe) {
      return
    }

    // Wait for the last finger to lift before judging the gesture.
    if (event.touches.length > 0) {
      swipe.cancelled = true

      return
    }

    swipeRef.current = null

    if (swipe.cancelled || !enabled) {
      return
    }

    const touch = event.changedTouches.item(0)

    if (!touch) {
      return
    }

    const dx = touch.clientX - swipe.startX
    const dy = touch.clientY - swipe.startY

    if (
      Math.abs(dx) < SWIPE_MIN_DISTANCE ||
      Math.abs(dx) < Math.abs(dy) * SWIPE_AXIS_RATIO
    ) {
      return
    }

    if (dx < 0) {
      onSwipeLeft()
    } else {
      onSwipeRight()
    }
  }

  const onTouchCancel = () => {
    swipeRef.current = null
  }

  return { onTouchStart, onTouchEnd, onTouchCancel }
}
